/* eslint-disable react-hooks/exhaustive-deps */
import { Close, Done, PriorityHigh } from '@mui/icons-material'
import { Button, Card, CardContent, Chip, Grid2 } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import NumberField from 'components/forms/NumberField'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import useSandreList from 'utils/customHook/useSandreList'
import useTitle from 'utils/customHook/useTitle'
import ToastrAction from 'toastr/actions/ToastrAction'
import PARModalCheck from './PARModalCheck'
import DtoPARParameters from 'survey/dto/DtoPARParameters'
import DtoLinksColumnPAR from 'survey/dto/DtoLinksColumnsPAR'
import DtoReadFileParams from 'import/dto/DtoReadFileParams'
import { nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import PARImportParameters from './PARImportParameters'
import { CardTitle } from 'components/card/NewCard'
import PropTypes from 'prop-types'
import { PAR_CONSTANTS } from 'agriAdministration/constants/AgriConstants'
import moment from 'moment'
import { H_AGRI_IMPORT_PAR } from 'account/constants/AccessRulesConstants'
import FileSelection from 'components/file/FileSelection'

const TitleWithIcon = ({ title, isValid, isError, errorLabel }) => {
    const getStateIcon = () => {
        if (isValid) return <Chip size='small' label={i18n.completed} color={'validStatus'} icon={<Done sx={{ fontSize: 16 }}/>}/>
        if (!isError) return <Chip size='small' label={i18n.toComplete} color={'warningStatus'} icon={<PriorityHigh sx={{ fontSize: 16 }}/>}/>
        return <Chip size='small' label={errorLabel ? errorLabel : `${i18n.error} : ${i18n.requiredField}`} color={'errorStatus'} icon={<Close sx={{ fontSize: 16 }}/>}/>
    }

    return (
        <Grid2 container size={12} alignItems='center' justifyContent={'space-between'} spacing={1}>
            <Grid2>{title}</Grid2>
            <Grid2>{getStateIcon()}</Grid2>
        </Grid2>
    )
}

TitleWithIcon.propTypes = {
    title: PropTypes.string.isRequired,
    isValid: PropTypes.bool,
    isError: PropTypes.bool,
    errorLabel: PropTypes.string,
}

const PARImportApp = () => {
    const [file, setFile] = useState([])
    const [isError, setIsError] = useState(false)
    const [openModalVerifyImport, setOpenModalVerifyImport] = useState(false)
    const [params, setParams] = useState({
        columnsData: [
            {
                columnNumber: undefined,
                lexic: PAR_CONSTANTS.idUsagerColumn.value,
            },
            {
                columnNumber: undefined,
                lexic: PAR_CONSTANTS.idPointDePrelevementColumn.value,
            },
            {
                columnNumber: undefined,
                lexic: PAR_CONSTANTS.idUsagePreviColumn.value,
            },
        ],
        hasVolumeAttribution: false,
        hasAuthorisedVolume: false,
        hasRequestedVolume: false,
        hasRequestedLowWaterVolume: false,
        hasAttributionComments: false,
        hasAutorisationComments: false,
        attributionDate: undefined,
        authorisationDate: undefined,
        askYear: moment().year(),
    })

    const columnsLexic = useSandreList('AGRI_PAR.IMPORT')

    const duplicatedColumns = params.columnsData.filter((fc, fi) => params.columnsData.some((sc, si) => fc.columnNumber !== undefined && fc.columnNumber === sc.columnNumber && fi !== si))

    const {
        survey,
        accountUser,
    } = useSelector((store) => ({
        survey: store.AgriReducer.survey,
        accountUser: store.AccountReducer.accountUser,
    }))

    const { id } = useParams()
    const dispatch = useDispatch()
    const parParameters = useMemo(() => ({
        base64data: file[0]?.content,
        fileName: file[0]?.name,
        params: new DtoReadFileParams({
            hasHeader: true,
        }),
        columnsValues: new DtoLinksColumnPAR({
            idUsagerColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.idUsagerColumn.value)?.columnNumber,
            idPointDePrelevementColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.idPointDePrelevementColumn.value)?.columnNumber,
            volumeDemandeColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.volumeDemandeColumn.value)?.columnNumber,
            volumeDemandeEtiageColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.volumeDemandeEtiageColumn.value)?.columnNumber,
            volumeAttribueColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.volumeAttribueColumn.value)?.columnNumber,
            volumeAttribueEtiageColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.volumeAttribueEtiageColumn.value)?.columnNumber,
            commentaireAttributionColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.commentaireAttributionColumn.value)?.columnNumber,
            volumeAutoriseColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.volumeAutoriseColumn.value)?.columnNumber,
            volumeAutoriseEtiageColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.volumeAutoriseEtiageColumn.value)?.columnNumber,
            commentaireAutorisationColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.commentaireAutorisationColumn.value)?.columnNumber,
            idUsagePreviColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.idUsagePreviColumn.value)?.columnNumber,
            debitAutorisationColumn: params.columnsData.find(col => col.lexic === PAR_CONSTANTS.debitAutorisationColumn.value)?.columnNumber,
        }),
        askYear: params.askYear,
        idSurvey: parseInt(id),
        loginAttribution: params.hasVolumeAttribution ? accountUser.login : null,
        dateAttribution: params.attributionDate ? params.attributionDate : null,
        loginAutorisation: params.hasAuthorisedVolume ? accountUser.login : null,
        dateAutorisation: params.authorisationDate ? params.authorisationDate : null,
    }), [file, params, id, accountUser])

    useTitle(() => [{
        title: i18n.planning,
        href: 'planning',
    }, {
        title: i18n.surveys,
        href: 'planning/surveys',
    }, {
        title: `${survey.name || ''} [${survey.year || ''}]`,
        href: `survey/${id}`,
    }, {
        title: i18n.importName,
        href: `survey/${id}/import`,
        habilitation: H_AGRI_IMPORT_PAR,
    }])

    useEffect(() => {
        setIsError(false)
    }, [params, file])

    const getLines = () => params.columnsData.map((column, i) => {
        const lexic = columnsLexic.find(col => col.code === column.lexic)
        return {
            column: <Grid2 sx={{ padding: '10px 0' }}>
                <NumberField
                    obligatory
                    title={i18n.columnNumber}
                    value={column.columnNumber}
                    onChange={(value) => setParams({
                        ...params,
                        columnsData: params.columnsData.map((col, j) => {
                            if (i !== j) return col
                            return { columnNumber: parseInt(value), lexic: col.lexic }
                        }),
                    })}
                    min={1}
                    error={duplicatedColumns.includes(column.columnNumber)}
                />
            </Grid2>,
            name: lexic.name,
            description: lexic.description,
        }
    })
    const isFileValid = !!file[0]
    const isFileLinkValid = params.columnsData.every((col) => col.columnNumber && col.lexic) && duplicatedColumns.length === 0
    const isSettingsValid = params.askYear
                            && (!params.hasVolumeAttribution || (params.hasVolumeAttribution && params.attributionDate))
                            && (!params.hasAuthorisedVolume || (params.hasAuthorisedVolume && params.authorisationDate))

    const validateImport = () => {
        if (!isFileValid || !isFileLinkValid || !isSettingsValid) {
            setIsError(true)
            dispatch(ToastrAction.error(i18n.pleaseCompleteAllRequiredField))
        } else {
            setOpenModalVerifyImport(true)
        }
    }

    return (
        <Grid2 container size={12} padding={2} spacing={2} justifyContent='space-between' alignItems={'stretch'}>
            <Grid2 size={6} >
                <Card sx={{ height: '100%' }}>
                    <CardTitle
                        title={<TitleWithIcon title={i18n.fileSelection} isError={isError} isValid={isFileValid} />}
                    />
                    <CardContent sx={{ height: 'calc(100% - 40px)' }}>
                        <FileSelection files={file} setFiles={setFile} fileAccept='.xls, .xlsx' />
                    </CardContent>
                </Card>
            </Grid2>
            <Grid2 size={6}>
                <Card sx={{ height: '100%' }}>
                    <CardTitle
                        title={<TitleWithIcon title={i18n.settings} isError={isError} isValid={isSettingsValid} />}
                    />
                    <CardContent sx={{ height: 'calc(100% - 40px)' }}>
                        <PARImportParameters
                            params={params}
                            setParams={setParams}
                        />
                    </CardContent>
                </Card>
            </Grid2>
            <Grid2 size={12}>
                <CardTable
                    title={<TitleWithIcon title={i18n.fileLink} isError={isError || duplicatedColumns.length > 0} isValid={isFileLinkValid} errorLabel={duplicatedColumns.length > 0 ? i18n.numColumnMustBeDifferent : undefined}/>}
                    hideNbElements
                    headers={['column', 'name', 'description']}
                    rows={getLines()}
                    rowsPerPageOptions={nbPerPageLabelMedium}
                />
            </Grid2>
            <Grid2 container size={12} justifyContent='center' alignItems='center' sx={{ height: 'min-content', padding: 2, position: 'sticky', bottom: 0, zIndex: 1000 }}>
                <Button
                    variant='contained'
                    startIcon={isError ? <Close /> : <Done />}
                    onClick={() => validateImport()}
                    sx={{
                        fontSize: '16px',
                        backgroundColor: isError ? 'errorStatus.contrastText' : 'primary',
                        color: '#fff',
                        borderRadius: '25px',
                        padding: '10px 20px',
                        width: 'auto',
                        position: 'relative',
                        '&:focus': {
                            backgroundColor: isError ? 'errorStatus.contrastText' : 'primary.grey',
                        },
                    }}
                >
                    {i18n.checkImportedtDatas}
                </Button>
            </Grid2>
            {openModalVerifyImport && <PARModalCheck
                open={openModalVerifyImport}
                onClose={() => {
                    setOpenModalVerifyImport(false)
                }}
                onEndJob={() => {
                    setFile([])
                    setParams({
                        ...params,
                        columnsData: params.columnsData.map((col) => ({ ...col, columnNumber: undefined })),
                    })
                }}
                importParameters={new DtoPARParameters(parParameters)}
            />}
        </Grid2>
    )
}

export default PARImportApp